/**
 * Helper fuer Web-Push-Subscription (Phase 5.6 von Todos-Notifications-Plan).
 *
 * Public-API:
 *   registerSW()            — registriert den Service-Worker bei /sw.js
 *   isSubscribed()          — true wenn aktuelle Browser-Session subscribed ist
 *   subscribePush()         — fragt Permission, holt VAPID-Key, registriert + meldet Backend
 *   unsubscribePush()       — entfernt Subscription lokal + Backend-seitig
 *   canUsePush              — true wenn Browser Push + Service-Worker + Notification kann
 */
import { api } from '@onvice/portal-shell-api';

export const canUsePush =
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!canUsePush) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.warn('[Push] SW-Registrierung fehlgeschlagen:', (err as Error).message);
    return null;
  }
}

export async function isSubscribed(): Promise<boolean> {
  if (!canUsePush) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return Boolean(sub);
}

export async function subscribePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!canUsePush) return { ok: false, reason: 'Browser unterstuetzt Push nicht' };
  const reg = await registerSW();
  if (!reg) return { ok: false, reason: 'Service-Worker nicht verfuegbar' };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'Permission abgelehnt' };
  }

  // VAPID-Public-Key vom Backend holen.
  const vapid = await api.get<{ success: boolean; publicKey: string }>('/notifications/vapid-public-key');
  if (!vapid.publicKey) return { ok: false, reason: 'VAPID-Key nicht erhalten' };

  // Existierende Subscription? Wenn sie zu unserem Key passt, weiterverwenden.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Vergleichen ist umstaendlich (ArrayBuffer); pragmatisch: bestehende verwerfen + neu anlegen.
    await sub.unsubscribe().catch(() => undefined);
  }
  sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
  });

  const json = sub.toJSON() as { endpoint: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return { ok: false, reason: 'Subscription-Daten unvollstaendig' };
  }
  await api.post('/notifications/push-subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return { ok: true };
}

export async function unsubscribePush(): Promise<void> {
  if (!canUsePush) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  // Multi-Device (2026-07-30): nur DIESES Geraet abmelden, nicht alle Subs
  // des Users. Endpoint aus dem lokalen Sub-Objekt an das Backend geben.
  const endpoint = sub?.endpoint;
  if (sub) await sub.unsubscribe().catch(() => undefined);
  try {
    await api.post('/notifications/push-unsubscribe', endpoint ? { endpoint } : {});
  } catch {
    /* ignore */
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}
